import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import React from "react";
import { Link, useParams } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp";
import { ArrowForward } from "@mui/icons-material";
import {
  FiUploadCloud,
  FiBarChart2,
  FiMessageSquare,
  FiMail,
  FiLayers,
  FiUsers,
  FiDatabase,
  FiCheckCircle,
  FiPieChart,
  FiUserCheck, // Added for Unique Contacts Sent
} from "react-icons/fi";
import BackBtn from "../../widgets/BackBtn";
import LeadsEnquiriesCard from "./LeadsEnquiriesCard";

const StatCard = ({ title, value, icon: Icon, color, link = "" }) => (
  <Paper
    elevation={2}
    sx={{
      p: 3,
      height: "100%",
      borderRadius: 2,
      background: `linear-gradient(135deg, ${color}10 0%, ${color}05 100%)`,
      borderLeft: `4px solid ${color}`,
      transition: "transform 0.2s, box-shadow 0.2s",
      position: "relative",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: 4,
        "& .view-link": {
          opacity: 1,
          transform: "translateX(0)",
        },
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
      <Box
        sx={{
          background: `${color}15`,
          p: 1,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={24} color={color} />
      </Box>
      <Typography
        variant="h6"
        sx={{ ml: 2, fontWeight: 600, color: "text.secondary" }}
      >
        {title}
      </Typography>
    </Box>

    <Typography
      variant="h4"
      sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}
    >
      {(value || "").toLocaleString()}
    </Typography>

    {link && (
      <Link
        to={link}
        style={{
          position: "absolute",
          bottom: 16,
          right: 16,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: color,
          textDecoration: "none",
          fontWeight: 600,
          fontSize: "0.875rem",
          transform: "translateX(8px)",
          transition: "opacity 0.2s, transform 0.2s",
          "&:hover": {
            textDecoration: "underline",
            opacity: 1,
          },
        }}
      >
        View
        <ArrowForward sx={{ fontSize: 16 }} />
      </Link>
    )}
  </Paper>
);

const AccountUsageDetails = ({ self = false }) => {
  const { id } = useParams();
  const { data, isLoading, isError, error } = useApiEp(
    !self ? `/root/account-usage/${id}` : `/root/self-account-usage`
  );

  if (isLoading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Alert severity="error">
          Error loading account usage: {error.message}
        </Alert>
      </Container>
    );
  }

  const stats = [
    {
      title: "Files Uploaded",
      value: data.filesUploadCount,
      icon: FiUploadCloud,
      color: "#6366F1",
    },
    {
      title: "Campaigns",
      value: data.campaignsCount,
      icon: FiBarChart2,
      color: "#10B981",
    },
    {
      title: "Chat Bots",
      value: data?.chatBotsCount ?? 0,
      icon: FiMessageSquare,
      color: "#F59E0B",
    },
    {
      title: "Messages Sent",
      value: data.messagesSentCount,
      icon: FiMail,
      color: "#3B82F6",
    },
    {
      title: "Messages Received",
      value: data.messagesReceivedCount,
      icon: FiMail,
      color: "#8B5CF6",
    },
    {
      title: "Templates Created",
      value: data.templatesCount,
      icon: FiLayers,
      color: "#EC4899",
    },
    ...(!self
      ? [
          {
            title: "Templates Used",
            value: data.templateUsedCount,
            icon: FiCheckCircle,
            color: "#58bd35",
            link: `/home/${id}/templates-used`,
          },
        ]
      : []),
    {
      title: "Contact Lists",
      value: data.contactsListCount,
      icon: FiUsers,
      color: "#06B6D4",
    },
    {
      title: "Marketing Contacts",
      value: data.marketingContactsCount,
      icon: FiDatabase,
      color: "#F97316",
    },
    {
      title: "Unique Contacts Sent",
      value: data.uniqueContactsSentCount,
      icon: FiUserCheck,
      color: "#D946EF", // Distinct color for new stat
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 5 }}>
      {!self && <BackBtn />}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            color: "text.primary",
            lineHeight: 1.2,
            textAlign: { xs: "center", md: "left" },
          }}
          aria-label={`System Usage for Customer ID: ${
            data?.customerId || "Unknown"
          }`}
        >
          System Usage for{" "}
          <span style={{ color: "#3d687d" }}>{data?.customerId || "N/A"}</span>
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Detailed overview of your account usage and statistics
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
            <StatCard
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              color={stat.color}
              link={stat.link || ""}
            />
          </Grid>
        ))}
      </Grid>
      {!self ? (
        <Box mt={4}>
          <LeadsEnquiriesCard id={id} />
        </Box>
      ) : (
        <></>
      )}

      <Paper
        elevation={2}
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 2,
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <FiPieChart size={24} color="#6366F1" />
          <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
            Summary
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Your account has sent {data.messagesSentCount.toLocaleString()}{" "}
          messages to {data.uniqueContactsSentCount.toLocaleString()} unique
          contacts and received {data.messagesReceivedCount.toLocaleString()}{" "}
          messages across {data.campaignsCount} campaigns. You've uploaded{" "}
          {data.filesUploadCount} files and created {data.templatesCount}{" "}
          templates.
        </Typography>
      </Paper>
    </Container>
  );
};

export default AccountUsageDetails;
